import React, { useState, useEffect } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import "../../css/DLT.css";
import { apiList } from "../../helpers/apiList";
import { axiosPostApi } from "../../helpers/interceptorServices";
import Pagination from "../common/Pagination";
import { BiEditAlt } from "react-icons/bi";
import { RiDeleteBinLine } from "react-icons/ri";
import { IoEyeOutline } from "react-icons/io5";
import SenderIDModal from "./SenderIDModal";
import DeleteConfirmationDltModal from "./DeleteConfirmationDltModal";
import { toast } from "react-toastify";
import CustomAlert from "../../helpers/CustomAlert";
import { useSelector } from "react-redux";
import { Popover, Transition } from "@headlessui/react";
import Button from "../customButton/Button";
import AddIcon from "../../../src/assets/Icons/AddIcon.svg";
import SuccessTick from "../../assets/Icons/correct-success-tick.png";

const SuccessModal = ({ isOpen, onClose, message }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-[16px] p-6 w-[505px] h-[290px] relative flex flex-col items-center justify-center">
        <div className="flex flex-col items-center">
          <img
            src={SuccessTick}
            alt="success icon"
            className="w-[77px] h-[77px] mb-4"
          />
          <h2 className="text-2xl font-semibold text-[#495057] mb-2">
            Success
          </h2>
          <p className="text-[#495057] font-semibold text-center mb-6">
            {message}
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 text-[#00b6ce] font-medium border-1 border-[#00b6ce] rounded-[8px] hover:bg-[#00b6ce] hover:text-white transition"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

const SenderIDs = ({ openModal, customerCode, name }) => {
  const loggedInUserName = sessionStorage.getItem("username");
  const [loading, setLoading] = useState(false);
  const [senders, setSenders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageDetails, setPageDetails] = useState(null);
  const [pageSize] = useState(10);
  const [openPopup, setOpenPopup] = useState(false);
  const [selectedSender, setSelectedSender] = useState(null);
  const [modalType, setModalType] = useState("");
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deletedId, setDeletedId] = useState(null);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    fetchSenderData(1);
  }, []);

  const handleViewSender = (sender, type) => {
    setSelectedSender({
      name: sender?.sendorName,
      id: sender?.sendorId,
      customerCode: sender?.customerCode,
      entityId: sender?.entityId,
    });
    setModalType(type);
    setOpenPopup(true);
  };

  const handleClose = () => {
    setOpenPopup(false);
    setOpenDeleteModal(false);
    setOpenSuccessModal(false);
  };

  const fetchSenderData = async (page) => {
    const data = {
      username: loggedInUserName,
      customerCode: customerCode,
    };

    try {
      setLoading(true);
      const response = await axiosPostApi(
        `${apiList.DLT_FETCH_SENDER}?pageSize=${pageSize}&pageNo=${page}`,
        data
      );
      setSenders(response?.data?.data || []);
      setPageDetails(response?.data?.pageDetails || null);
    } catch (error) {
      console.error("Failed to fetch sender data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSwitch = () => {
    openModal();
  };

  const handleDeleteId = (sender) => {
    setDeletedId({
      username: loggedInUserName,
      customerCode: sender?.customerCode,
      sendorId: sender?.sendorId,
      status: "3",
    });
    setOpenDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const response = await axiosPostApi(apiList.DELETE_SENDORID, deletedId);
      if (response?.status === 200 && response.data.status === true) {
        setSuccessMessage("Sender Deleted Successfully");
        setOpenSuccessModal(true);
        handleClose();
        await fetchSenderData(1);
      } else if (response.data.respCode === "01") {
        CustomAlert({
          title: "Data Submission Error",
          text:
            response.data.message || "Please check the details and try again.",
          icon: "error",
          confirmButtonText: "Retry",
        });
        handleClose();
      }
    } catch (error) {
      CustomAlert({
        title: "Error",
        text: "An error occurred while deleting the sender. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSenderUpdate = async () => {
    setSuccessMessage("Sender Updated Successfully");
    setOpenSuccessModal(true);
    await fetchSenderData(1);
  };

  const getActionItems = (sender) => {
    const actions = [];

    if (user?.data?.userRole === "CUST") {
      actions.push({
        icon: <BiEditAlt style={{ marginRight: "10px" }} />,
        label: "Edit",
        onClick: (e) => {
          e.stopPropagation();
          handleViewSender(sender, "modify");
        },
      });
    }

    actions.push({
      icon: <IoEyeOutline style={{ marginRight: "10px" }} />,
      label: "View",
      onClick: (e) => {
        e.stopPropagation();
        handleViewSender(sender, "view");
      },
    });

    if (user?.data?.userRole === "CUST") {
      actions.push({
        icon: <RiDeleteBinLine style={{ marginRight: "10px" }} />,
        label: "Delete",
        onClick: (e) => {
          e.stopPropagation();
          handleDeleteId(sender);
        },
      });
    }

    return actions;
  };

  return (
    <div className="sender-ids-container">
      <div className="flex first-tab-controls justify-end">
        {user?.data?.userRole === "CUST" && (
          <Button
            label="Add"
            icon={AddIcon}
            onClick={handleSwitch}
            className="pr-2"
          />
        )}
      </div>

      {loading ? (
        <div className="loading-container">
          <CircularProgress />
        </div>
      ) : (
        <div className="table-container !bg-[#FFFFFF] !shadow-none !px-0">
          {senders.length > 0 ? (
            <>
              <div className="table-wrapper">
                <table className="custom-table">
                  <thead className="bg-[#F1FFFF]">
                    <tr>
                      <th className="!border-b-0">Sr No</th>
                      <th className="!border-b-0">Entity ID</th>
                      <th className="!border-b-0">Sender ID</th>
                      <th className="!border-b-0">Sender Name</th>
                      <th className="!border-b-0">Status</th>
                      <th className="!border-b-0">Created Date-Time</th>
                      <th className="!border-b-0">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {senders.map((sender, index) => (
                      <tr key={sender.sendorId}>
                        <td>{(currentPage - 1) * pageSize + index + 1}</td>
                        <td>{sender.entityId || "-"}</td>
                        <td>{sender.sendorId || "-"}</td>
                        <td>{sender.sendorName || "-"}</td>
                        <td>
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border`}
                            style={{
                              backgroundColor:
                                sender.status === "ACTIVE"
                                  ? "#e6f9ec"
                                  : sender.status === "BLOCKED"
                                  ? "#fdecea"
                                  : "#f0f0f0",
                              borderColor:
                                sender.status === "ACTIVE"
                                  ? "#55cd69"
                                  : sender.status === "BLOCKED"
                                  ? "#fb4a3f"
                                  : "#6c757d",
                              color:
                                sender.status === "ACTIVE"
                                  ? "#2e9b47"
                                  : sender.status === "BLOCKED"
                                  ? "#d4352c"
                                  : "#6c757d",
                            }}
                          >
                            <span
                              className="w-2 h-2 rounded-full mr-2"
                              style={{
                                backgroundColor:
                                  sender.status === "ACTIVE"
                                    ? "#55cd69"
                                    : sender.status === "BLOCKED"
                                    ? "#fb4a3f"
                                    : "#6c757d",
                              }}
                            ></span>
                            {sender.status || "-"}
                          </span>
                        </td>
                        <td>{sender.createdDate || "-"}</td>
                        <td className="actions" style={{ overflow: "visible" }}>
                          <Popover>
                            {({ open, close }) => (
                              <>
                                <Popover.Button
                                  style={{
                                    cursor: "pointer",
                                    fontSize: "20px",
                                    display: "inline-block",
                                    padding: "0 10px",
                                    background: "none",
                                    border: "none",
                                  }}
                                >
                                  ⋮
                                </Popover.Button>
                                <Transition
                                  as={React.Fragment}
                                  enter="transition ease-out duration-200"
                                  enterFrom="opacity-0 translate-y-1"
                                  enterTo="opacity-100 translate-y-0"
                                  leave="transition ease-in duration-150"
                                  leaveFrom="opacity-100 translate-y-0"
                                  leaveTo="opacity-0 translate-y-1"
                                >
                                  <Popover.Panel
                                    style={{
                                      position: "absolute",
                                      zIndex: 2000,
                                      minWidth: "220px",
                                      backgroundColor: "#ffffff",
                                      boxShadow: "0 8px 16px rgba(0,0,0,0.25)",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "8px",
                                      padding: "8px 0",
                                      overflow: "visible",
                                    }}
                                  >
                                    {getActionItems(sender).map(
                                      (action, idx) => (
                                        <div
                                          key={idx}
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            padding: "10px 20px",
                                            cursor: "pointer",
                                            color: "#333",
                                            fontSize: "15px",
                                            fontWeight: "500",
                                            transition: "background-color 0.2s",
                                            whiteSpace: "nowrap",
                                          }}
                                          onMouseEnter={(e) =>
                                            (e.currentTarget.style.backgroundColor =
                                              "#f0f0f0")
                                          }
                                          onMouseLeave={(e) =>
                                            (e.currentTarget.style.backgroundColor =
                                              "#ffffff")
                                          }
                                          onClick={(e) => {
                                            action.onClick(e);
                                            close();
                                          }}
                                        >
                                          {action.icon}
                                          <span>{action.label}</span>
                                        </div>
                                      )
                                    )}
                                  </Popover.Panel>
                                </Transition>
                              </>
                            )}
                          </Popover>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3">
                <Pagination
                  currentPage={currentPage}
                  totalCount={pageDetails?.totalRecords || 0}
                  pageSize={pageSize}
                  onPageChange={(page) => {
                    setCurrentPage(page);
                    fetchSenderData(page);
                  }}
                />
              </div>
            </>
          ) : (
            <p className="no-data-message">No data available</p>
          )}
        </div>
      )}
      <SenderIDModal
        show={openPopup}
        handleClose={handleClose}
        customerCode={customerCode}
        name={name}
        senderDetails={selectedSender}
        modalType={modalType}
        handleShowTable={handleSenderUpdate}
      />
      <DeleteConfirmationDltModal
        isOpen={openDeleteModal}
        onClose={handleClose}
        handleDelete={handleDelete}
        loading={loading}
      />
      <SuccessModal
        isOpen={openSuccessModal}
        onClose={handleClose}
        message={successMessage}
      />
    </div>
  );
};

export default SenderIDs;
